import React, { useContext, useMemo, useState } from 'react';
import flush from 'just-flush';
import z from 'zod';
import {
  Body,
  ContactForm, ContactFormOfficeInfo, ErrorMessage, Heading,
} from '@stereoagency/ui';
import { useQuery } from 'urql';
import useTranslation from 'next-translate/useTranslation';
import Turnstile from 'react-turnstile';
import { FindAllOfficesQueryDocument } from '@/gql/client/graphql';
import { useFragment } from '@/gql';
import { OfficeFieldsFragment } from '@/gql/documents/fragments';
import { LinesContext } from '@/components/LinesContext';
import { useLangCodeFilter } from '@/hooks';
import { parseHtml } from '@/utils';

type Props = {
  data: {
    label?: string
    title?: string
    description?: string
    email_target?: 'contact' | 'hacked'
    display_office_information?: boolean
  }
};

const schema = z.object({
  name: z.string().min(1),
  organisation: z.string(),
  email: z.string().email(),
  phone: z.string(),
  message: z.string().min(1),
  optin: z.boolean(),
  $turnstile_key: z.string().min(1),
});

export default function NvisoContactForm({
  data: {
    label, title, description, email_target, display_office_information,
  },
}: Props) {
  const { enableLines } = useContext(LinesContext);
  const { t } = useTranslation();
  const [success, setSuccess] = useState<boolean | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const langCode = useLangCodeFilter();
  const [{ data: officesQuery }] = useQuery({
    query: FindAllOfficesQueryDocument,
    variables: { lang: langCode },
  });
  const officesFields = useFragment(OfficeFieldsFragment, flush(officesQuery?.offices?.nodes ?? []));

  const officeInformation = useMemo<ContactFormOfficeInfo[]>(() => flush(officesFields.map((entry) => {
    if (!entry.cptOfficeCustomFields?.country) {
      return null;
    }

    const emailList = flush(entry.cptOfficeCustomFields.emailaddresses?.split(/\r|\n/) ?? []);
    const phoneList = flush(entry.cptOfficeCustomFields.phonenumbers?.split(/\r|\n/) ?? []);

    return {
      country: t(`countries:${entry.cptOfficeCustomFields.country}`),
      info: (
        <div className="flex flex-col items-start whitespace-pre-wrap">
          {entry.cptOfficeCustomFields.address}
          <br />
          {emailList.map((email) => (
            <a key={email} href={`mailto:${email}`}>{email}</a>
          ))}
          <br />
          {phoneList.map((phone) => (
            <a key={phone} href={`tel:${phone.replace(/\s/g, '')}`}>{phone}</a>
          ))}
        </div>
      ),
    };
  })), [officesFields]);

  const handleSubmit = async (values: z.infer<typeof schema>) => {
    try {
      setSubmitting(true);
      const response = await fetch('/api/contact', {
        method: 'POST',
        body: JSON.stringify({
          ...values,
          email_target: email_target ?? 'contact',
          debug: process.env.NODE_ENV === 'development',
        }),
        headers: { 'content-type': 'application/json' },
      }).then((r) => r.json()) as {
        success: boolean
        errors: Record<string, string[]>
      };
      setSuccess(response.success);
    } catch (err) {
      setSuccess(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ContactForm
      enableLines={enableLines}
      label={label}
      title={title}
      description={description ? parseHtml(description) : undefined}
      officeInformation={display_office_information ? officeInformation : undefined}
      submitContent={t('contact-form:submit')}
      isSubmitting={submitting}
      formConfig={{
        id: 'contact',
        schema,
        config: [
          [{
            name: 'name',
            type: 'input',
            label: t('contact-form:fields.name.label'),
            placeholder: t('contact-form:fields.name.placeholder'),
          }],
          [{
            name: 'organisation',
            type: 'input',
            label: t('contact-form:fields.organisation.label'),
            placeholder: t('contact-form:fields.organisation.placeholder'),
          }],
          [{
            name: 'email',
            type: 'email',
            label: t('contact-form:fields.email.label'),
            placeholder: t('contact-form:fields.email.placeholder'),
          }],
          [{
            name: 'phone',
            type: 'input',
            label: t('contact-form:fields.phone.label'),
            placeholder: t('contact-form:fields.phone.placeholder'),
          }],
          [{
            name: 'message',
            type: 'textarea',
            label: t('contact-form:fields.message.label'),
            placeholder: t('contact-form:fields.message.placeholder'),
          }],
          [{
            name: 'optin',
            type: 'checkbox',
            label: t('contact-form:fields.optin.label'),
          }],
          [{
            name: '$turnstile_key',
            type: 'custom',
            label: t('contact-form:fields.turnstile.label'),
            render: (props, errors) => (
              <div>
                <Turnstile
                  sitekey={process.env.TURNSTILE_SITE_KEY ?? ''}
                  onVerify={(value) => {
                    props.onBlur({ target: { name: '$turnstile_key' } });
                    props.onChange({ target: { name: '$turnstile_key', value } });
                  }}
                />
                {!!errors?.$turnstile_key?.message && (
                  <ErrorMessage className="mt-3">{errors.$turnstile_key.message}</ErrorMessage>
                )}
              </div>
            ),
          }],
        ],
      }}
      errorContent={success === false ? t('contact-form:error.generic') : null}
      successContent={success ? (
        <div>
          <Heading variant="h3">{t('contact-form:success.title')}</Heading>
          <Body className="mt-3">{t('contact-form:success.description')}</Body>
        </div>
      ) : null}
      onSubmit={handleSubmit}
    />
  );
}
